import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ArrowRight, Check, X, ClipboardCheck, FileText, Wrench, Leaf } from "lucide-react";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Reveal, MaskedLine, SectionLabel } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";

const SUITABLE = [
  { ok: true, title: "Well-insulated home", text: "Loft and cavity insulation done? A heat pump will shine." },
  { ok: true, title: "Off the gas grid", text: "On oil or LPG in rural Devon? A heat pump usually wins on running costs." },
  { ok: true, title: "Underfloor or large radiators", text: "Heat pumps run warm-and-steady, not hot-and-fast. Bigger emitters suit them." },
  { ok: false, title: "Poorly insulated? Boiler may win", text: "We'll tell you straight — sometimes insulate first, heat pump later." },
];

const STEPS = [
  { icon: ClipboardCheck, title: "Free home survey", text: "We assess insulation, radiators, hot water and electrics — no obligation." },
  { icon: FileText, title: "Fixed quote & grant check", text: "One honest price, plus we'll flag any government grants your home qualifies for." },
  { icon: Wrench, title: "Install & annual servicing", text: "Fitted, commissioned and registered — then serviced by us every year after." },
];

export default function HeatPumps() {
  useEffect(() => {
    document.title = "Air Source Heat Pumps — Fitted & Serviced in South Devon | Teign Gas";
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", "Air source heat pump installation and servicing across Exeter, Newton Abbot, Torquay & South Devon. Free home survey, fixed quotes, grant advice. Teign Gas.");
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "hp-schema";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Air source heat pump installation and servicing",
      "provider": { "@type": ["Plumber", "HVACBusiness"], "name": "Teign Gas", "telephone": "+447737016265" },
      "areaServed": "South Devon",
    });
    document.head.appendChild(schema);
    return () => schema.remove();
  }, []);

  return (
    <div data-testid="heat-pumps-page" className="bg-obsidian text-neutral-100 overflow-x-clip">
      <div className="grain-overlay" />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="hp-logo" className="flex items-center shrink-0">
            <img src="/images/logo-flame.png" alt="Teign Gas" className="h-9 w-9 sm:hidden mix-blend-screen" />
            <img src="/images/logo-mark.png" alt="Teign Gas — Service, Repair, Install" className="hidden sm:block h-9 w-auto mix-blend-screen" />
          </Link>
          <div className="flex items-center gap-3">
            <a href={BUSINESS.phoneHref} data-testid="hp-phone-link" className="hidden sm:flex items-center gap-2 font-mono text-xs tracking-wider text-neutral-300 border border-neutral-700 px-4 py-2.5 rounded-sm hover:border-torch hover:text-torch transition-colors">
              <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
            </a>
            <Link to="/#quote" data-testid="hp-quote-btn" className="bg-torch hover:bg-torch-hover text-white font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 rounded-sm transition-all whitespace-nowrap">
              Free Survey
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-torch font-semibold flex items-center gap-2">
                <Leaf className="w-4 h-4" /> Air source heat pumps
              </p>
              <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-white mt-4">
                <MaskedLine delay={0.1}>Heat from</MaskedLine>
                <MaskedLine delay={0.25}>thin air.</MaskedLine>
                <MaskedLine delay={0.4}><span className="text-torch">Seriously.</span></MaskedLine>
              </h1>
              <Reveal delay={0.55}>
                <p className="mt-7 max-w-xl text-base sm:text-lg text-neutral-400 leading-relaxed">
                  Teign Gas fits and services air source heat pumps across South Devon.
                  A heat pump moves warmth from the outside air into your home — delivering
                  around three units of heat for every unit of electricity. We survey first,
                  quote honestly, and tell you if your house suits one. If it doesn't, we'll say so.
                </p>
              </Reveal>
              <Reveal delay={0.7}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/#quote" data-testid="hp-hero-quote-btn" className="group bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-all hover:shadow-[0_0_36px_rgba(255,85,0,0.5)]">
                    Book my free survey
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="hp-hero-whatsapp-btn" className="border border-neutral-700 hover:border-torch text-neutral-200 hover:text-torch font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-colors">
                    <MessageCircle className="w-4 h-4" /> Ask a question
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.3} className="relative hidden lg:block">
              <div className="absolute -inset-3 border border-torch/40 translate-x-5 translate-y-5 pointer-events-none" />
              <div className="relative overflow-hidden rounded-sm">
                <img src="/images/heatpump-house.jpg" alt="Modern home with an air source heat pump installed by Teign Gas" className="w-full h-[480px] object-cover grayscale-[25%] contrast-125" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-[#0C0C0C] border-y border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <Reveal><SectionLabel>Is your home suitable?</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white mt-4 mb-12">
                The honest checklist.
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
              {SUITABLE.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08} className="h-full">
                  <div className={`h-full bg-surface border rounded-sm p-6 transition-colors ${s.ok ? "border-neutral-800 hover:border-torch" : "border-neutral-800 hover:border-amber-500/60"}`}>
                    {s.ok ? <Check className="w-5 h-5 text-torch mb-4" strokeWidth={3} /> : <X className="w-5 h-5 text-amber-500 mb-4" strokeWidth={3} />}
                    <h3 className="font-display font-bold text-white leading-snug">{s.title}</h3>
                    <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {STEPS.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.1}>
                  <div className="flex items-start gap-4 border-t-2 border-torch/60 pt-5">
                    <span className="font-mono font-bold text-3xl text-torch/25">0{i + 1}</span>
                    <div>
                      <h3 className="font-display font-bold text-white">{s.title}</h3>
                      <p className="mt-1.5 text-sm text-neutral-400 leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="relative overflow-hidden rounded-sm border border-neutral-800">
                <img src="/images/heatpump-unit.jpg" alt="Air source heat pump outdoor unit" loading="lazy" className="w-full h-72 lg:h-96 object-cover grayscale-[25%] contrast-125" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white">
                  Already have a heat pump?
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 text-neutral-400 leading-relaxed">
                  We service them too — refrigerant and pressure checks, filter cleans, and
                  system health checks that keep efficiency high and breakdowns rare. An
                  unserviced heat pump quietly loses a chunk of its efficiency every year.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-7 flex flex-wrap gap-4 items-center">
                  <Link to="/#quote" data-testid="hp-service-quote-btn" className="border border-torch/50 hover:bg-torch/10 text-torch font-display font-bold uppercase tracking-wide px-6 py-3.5 rounded-sm transition-colors">
                    Book a service quote
                  </Link>
                  <Link to="/blog/heat-pumps-south-devon" data-testid="hp-article-link" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-400 hover:text-torch transition-colors">
                    Read: Heat Pumps in South Devon →
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
    </div>
  );
}
